/**
 * Live Data Mock Component
 * Shows animated "live" data with specific ranges and timings
 * Displays "Pulling Live Data..." indicator with progress bars
 */

import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import KPIWidget from '../common/KPIWidget';

const LiveDataMock: React.FC = () => {
  const [websiteScraped, setWebsiteScraped] = useState(301);
  const [potentialLeads, setPotentialLeads] = useState(65);
  const [productsInCatalog, setProductsInCatalog] = useState(3001);
  const [activeCustomers, setActiveCustomers] = useState(3);
  const [isLive, setIsLive] = useState(true);
  
  const [websiteProgress, setWebsiteProgress] = useState(0);
  const [leadsProgress, setLeadsProgress] = useState(0);
  const [productsProgress, setProductsProgress] = useState(0);
  const [customersProgress, setCustomersProgress] = useState(0);

  useEffect(() => {
    // Website Scraped: 301-3000, +1 every 3 minutes
    const websiteInterval = setInterval(() => {
      setWebsiteScraped(prev => {
        const newVal = prev + 1;
        return newVal > 3000 ? 301 : newVal;
      });
      setWebsiteProgress(prev => (prev + 10) % 100);
    }, 180000); // 3 minutes

    // Potential Leads: 65-300, +2 every 2 minutes
    const leadsInterval = setInterval(() => {
      setPotentialLeads(prev => {
        const newVal = prev + 2;
        return newVal > 300 ? 65 : newVal;
      });
      setLeadsProgress(prev => (prev + 10) % 100);
    }, 120000); // 2 minutes

    // Products in Catalog: 3001-9000, +6 every 3 minutes
    const productsInterval = setInterval(() => {
      setProductsInCatalog(prev => {
        const newVal = prev + 6;
        return newVal > 9000 ? 3001 : newVal;
      });
      setProductsProgress(prev => (prev + 10) % 100);
    }, 180000); // 3 minutes

    // Active Customers: 3-900, random 2 numbers every 1 minute
    const customersInterval = setInterval(() => {
      setActiveCustomers(prev => {
        const change = Math.floor(Math.random() * 5) - 2; // -2 to +2
        const newVal = prev + change;
        return Math.max(3, Math.min(900, newVal));
      });
      setCustomersProgress(prev => (prev + 10) % 100);
    }, 60000); // 1 minute

    // Blink live indicator every second
    const blinkInterval = setInterval(() => {
      setIsLive(prev => !prev);
    }, 1000);

    return () => {
      clearInterval(websiteInterval);
      clearInterval(leadsInterval);
      clearInterval(productsInterval);
      clearInterval(customersInterval);
      clearInterval(blinkInterval);
    };
  }, []);

  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US');
  };

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header with Live Indicator */}
      <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, margin: 0 }}>
          📊 Live Analytics Dashboard
        </h1>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          padding: '0.5rem 1rem',
          background: 'var(--brand-green)',
          borderRadius: '20px',
          animation: isLive ? 'pulse 0.3s ease' : 'none'
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: '#00ff00',
            boxShadow: '0 0 10px #00ff00',
            animation: 'blink 1s infinite'
          }} />
          <span style={{ 
            fontWeight: 700, 
            fontSize: '0.875rem',
            color: 'var(--text-dark)'
          }}>
            Pulling Live Data...
          </span>
        </div>
      </div>

      {/* Live KPIs */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div className="glass-card" style={{ 
          padding: '1.5rem',
          border: '2px solid var(--brand-purple)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '0.7rem', color: 'var(--brand-green)', fontWeight: 700 }}>
            ● LIVE
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Website Scraped
          </div>
          <div style={{ 
            fontSize: '2rem', 
            fontWeight: 800, 
            color: 'var(--brand-green)',
            fontFamily: 'monospace',
            letterSpacing: '-1px'
          }}>
            {formatNumber(websiteScraped)}
          </div>
          <div style={{ marginTop: '0.75rem', background: '#0f172a', borderRadius: '4px', overflow: 'hidden', height: '8px' }}>
            <div style={{ 
              width: `${websiteProgress}%`, 
              height: '100%', 
              background: 'linear-gradient(90deg, var(--brand-green), var(--brand-purple))',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        <div className="glass-card" style={{ 
          padding: '1.5rem',
          border: '2px solid var(--brand-purple)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '0.7rem', color: 'var(--brand-green)', fontWeight: 700 }}>
            ● LIVE
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Total Potential Leads
          </div>
          <div style={{ 
            fontSize: '2rem', 
            fontWeight: 800, 
            color: 'var(--brand-peach)',
            fontFamily: 'monospace',
            letterSpacing: '-1px'
          }}>
            {formatNumber(potentialLeads)}
          </div>
          <div style={{ marginTop: '0.75rem', background: '#0f172a', borderRadius: '4px', overflow: 'hidden', height: '8px' }}>
            <div style={{ 
              width: `${leadsProgress}%`, 
              height: '100%', 
              background: 'linear-gradient(90deg, var(--brand-peach), #ff6b6b)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        <div className="glass-card" style={{ 
          padding: '1.5rem',
          border: '2px solid var(--brand-purple)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '0.7rem', color: 'var(--brand-green)', fontWeight: 700 }}>
            ● LIVE
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Products in Catalog
          </div>
          <div style={{ 
            fontSize: '2rem', 
            fontWeight: 800, 
            color: 'var(--brand-purple)',
            fontFamily: 'monospace',
            letterSpacing: '-1px'
          }}>
            {formatNumber(productsInCatalog)}
          </div>
          <div style={{ marginTop: '0.75rem', background: '#0f172a', borderRadius: '4px', overflow: 'hidden', height: '8px' }}>
            <div style={{ 
              width: `${productsProgress}%`, 
              height: '100%', 
              background: 'linear-gradient(90deg, var(--brand-purple), #9d4edd)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        <div className="glass-card" style={{ 
          padding: '1.5rem',
          border: '2px solid var(--brand-purple)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '0.7rem', color: 'var(--brand-green)', fontWeight: 700 }}>
            ● LIVE
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Active Customers
          </div>
          <div style={{ 
            fontSize: '2rem', 
            fontWeight: 800, 
            color: '#00d4ff',
            fontFamily: 'monospace',
            letterSpacing: '-1px'
          }}>
            {formatNumber(activeCustomers)}
          </div>
          <div style={{ marginTop: '0.75rem', background: '#0f172a', borderRadius: '4px', overflow: 'hidden', height: '8px' }}>
            <div style={{ 
              width: `${customersProgress}%`, 
              height: '100%', 
              background: 'linear-gradient(90deg, #00d4ff, #06b6d4)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>

      {/* Live Activity Feed */}
      <Card title="Live Activity Stream" icon="📡">
        <div style={{ padding: '1rem' }}>
          <div style={{ 
            background: '#0f172a', 
            padding: '1rem', 
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            color: '#00ff00',
            maxHeight: '300px',
            overflow: 'auto'
          }}>
            <div style={{ marginBottom: '0.5rem', opacity: 0.7 }}>
              [15:34:33] → Pulling Live Data from Shopify API...
            </div>
            <div style={{ marginBottom: '0.5rem', opacity: 0.8 }}>
              [15:34:34] → Syncing {formatNumber(potentialLeads)} potential leads...
            </div>
            <div style={{ marginBottom: '0.5rem', opacity: 0.9 }}>
              [15:34:35] → Processing {formatNumber(productsInCatalog)} products...
            </div>
            <div style={{ marginBottom: '0.5rem', color: '#ffd6a5' }}>
              [15:34:36] → Website scraped: {formatNumber(websiteScraped)} pages
            </div>
            <div style={{ marginBottom: '0.5rem', color: '#b8d58d' }}>
              [15:34:37] → Customer database: {formatNumber(activeCustomers)} active
            </div>
            <div style={{ marginBottom: '0.5rem', opacity: 1, color: '#00ff00', fontWeight: 700 }}>
              [15:34:38] ✓ Live data stream active
            </div>
            <div style={{ animation: 'blink 1s infinite' }}>
              [15:34:39] ▊ Streaming...
            </div>
          </div>
          <div style={{ 
            marginTop: '1rem', 
            fontSize: '0.75rem', 
            color: 'var(--text-muted)',
            textAlign: 'center'
          }}>
            🔄 Auto-refreshing every 150ms • Connected to live data sources
          </div>
        </div>
      </Card>

      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.3; }
        }
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default LiveDataMock;
